import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import Langs, { Localize } from "../../../app/lang/langs";
import { vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * V-Learning — a lesson whose content is a YouTube video.
 *
 * The frame is built from `lesson.youtube_id` and nothing else. The backend stores an
 * eleven-character video id rather than a URL precisely so that no stored value can ever
 * decide the origin of an iframe on this page: if the field held a URL, an author (or a bad
 * row) could point this frame at any site on the internet and have it render inside the
 * platform's chrome. Interpolating an id into a fixed youtube-nocookie.com template makes
 * that class of problem impossible rather than unlikely, so the id is re-validated here too
 * before it is used.
 */

// The eleven-character shape YouTube ids take. Enforced client-side as well as server-side
// because this value is about to become part of a URL — a defence worth having twice.
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/

// The kit has no focus token, and every interactive element in this module needs a visible
// ring for keyboard users.
const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

// How often the wall-clock heartbeat fires, in seconds.
const HEARTBEAT_SECONDS = 5

export default function YouTubeLesson({ lesson, onProgress }) {

    const youtubeId = lesson && YOUTUBE_ID.test(String(lesson.youtube_id || ''))
        ? String(lesson.youtube_id) : null

    // `onProgress` is called on a wall-clock interval rather than from real playback events.
    // A cross-origin iframe reports nothing to its parent, and the only reliable watch-time
    // signal YouTube offers is its IFrame Player API — a third-party script this project is
    // not adding. Time-with-the-lesson-open is therefore the honest measure available, and it
    // is the same measure the backend clamps: `heartbeat` refuses more seconds than have
    // actually elapsed, so an over-reporting client gains nothing.
    const progressRef = useRef(onProgress)
    progressRef.current = onProgress

    useEffect(() => {
        if (!youtubeId) return undefined
        const timer = setInterval(() => {
            if (progressRef.current) progressRef.current(HEARTBEAT_SECONDS, null)
        }, HEARTBEAT_SECONDS * 1000)
        return () => clearInterval(timer)
    }, [youtubeId])

    if (!youtubeId) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_YOUTUBE_MISSING" />
                </Typography>
            </Box>
        )
    }

    const src = `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1`

    return (
        <Box>
            <Box sx={{
                ...vlearnSurface.card,
                aspectRatio: '16 / 9',
                width: '100%',
                backgroundColor: '#000',
                '& iframe': { display: 'block', width: '100%', height: '100%', border: 0 }
            }}>
                <iframe
                    src={src}
                    title={Localize('VLEARN_VIDEO_FRAME_TITLE').replace('%title%', lesson.title || '')}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    // `referrerPolicy` keeps the platform's own URLs — which carry course and
                    // lesson ids — out of YouTube's request logs.
                    referrerPolicy="strict-origin-when-cross-origin"
                    // The player genuinely needs scripts, its own storage and presentation
                    // access to play. `allow-same-origin` here means same-origin as
                    // youtube-nocookie.com, not as VPlatform, so it grants the frame nothing
                    // on this page. What the sandbox does withhold is what a video has no
                    // business doing: navigating the top window, submitting forms, starting
                    // downloads and locking the pointer.
                    sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
                />
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    size="small"
                    component="a"
                    href={`https://www.youtube.com/watch?v=${youtubeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                    sx={{ ...focusRing, textTransform: 'none', fontSize: '0.8rem', color: 'var(--text-secondary, #4b5263)' }}
                >
                    <Langs str="VLEARN_WATCH_ON_YOUTUBE" />
                </Button>
            </Box>
        </Box>
    )
}
